import React, { useEffect, useState } from 'react';

import { Loader, RotateCcw, Trash2 } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { toast } from 'react-toastify';

import { SortableTableHeader } from '#/common/components';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
    Badge,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from '#/components/ui';
import { useAppContext } from '#/context';
import { apiEndpoints, queryKeys, useDeleteMutation, useInfiniteScrollQuery, usePostMutation } from '#/services';
import { TEAMS_TABLE_COL_GROUP, TEAMS_TABLE_HEADERS } from '#/utils';
import { AddTeamMember, DeleteTeamConfirmation, TeamStatusBadge } from '..';

const Teams = () => {
    const { user } = useAppContext();
    const { ref, inView } = useInView();
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const { isOwner: globalOwner } = user || {};
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteScrollQuery({
        key: queryKeys.TEAMS,
        url: apiEndpoints.GET_TEAMS,
    });
    const { mutate: removeMember, isLoading: deleteApiLoading } = useDeleteMutation({
        url: apiEndpoints.DELETE_TEAM_MEMBER(deleteId),
        queryKey: [queryKeys.TEAMS],
        successMessage: 'Member removed successfully!',
        onSuccess: handleRemoveContactSuccess,
    });

    const { mutate: addTeamMemberApiCall, isLoading: addTeamMemberApiLoading } = usePostMutation({
        url: apiEndpoints.RESEND_INVITE,
        queryKey: [queryKeys.TEAMS],
        errorMessage: 'Failed to send invitation!',
        onSuccess: handleReinviteSuccess,
    });
    function handleReinviteSuccess(resp) {
        toast.success(resp?.detail || 'Invitation sent successfully!');
    }
    function handleRemoveContactSuccess() {
        setOpenConfirmDialog(false);
        setDeleteId(null);
    }

    const teamMembers = data?.pages.flatMap(page => page.results) ?? [];

    const handleDeleteMember = id => () => {
        setOpenConfirmDialog(true);
        setDeleteId(id);
    };

    const handleResendInvite = email => () => {
        addTeamMemberApiCall({
            payload: {
                email: email,
            },
        });
    };

    const handleConfirmDelete = () => {
        if (deleteId) {
            removeMember({});
        }
    };
    useEffect(() => {
        if (inView && hasNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, fetchNextPage]);
    const renderTeamAction = (isOwner, status, id, email) => {
        if (isOwner) return '-';

        if (status === 'JOINED' || status === 'INVITED') {
            return (
                <button onClick={handleDeleteMember(id)}>
                    <Trash2 className='w-4 h-4 text-gray500 hover:text-red-700' />
                </button>
            );
        }

        if (status === 'EXPIRED') {
            return (
                <div className='flex gap-2'>
                    <button onClick={handleResendInvite(email)} disabled={addTeamMemberApiLoading}>
                        <RotateCcw className='w-4 h-4 text-blue-500 hover:text-blue-700' />
                    </button>
                    <button onClick={handleDeleteMember(id)}>
                        <Trash2 className='w-4 h-4 text-gray500 hover:text-red-700' />
                    </button>
                </div>
            );
        }

        return '-';
    };

    return (
        <>
            <div className='flex flex-col min-h-[calc(100dvh-235px)] text-xl font-medium px-8 py-7'>
                <div className='flex flex-col border rounded-lg '>
                    <div className='flex justify-between p-6'>
                        <div className='flex items-center gap-2'>
                            <h3 className='font-medium text-lg text-gray900'>Team Members</h3>
                            <Badge className='bg-blue50 px-2 py-[2px] min-w-8 h-5 text-blue700 font-medium text-xs'>
                                {teamMembers?.length || 0}
                            </Badge>
                        </div>
                        {globalOwner && <AddTeamMember />}
                    </div>
                    <div className='overflow-x-auto'>
                        <Table className='w-full table-fixed'>
                            {TEAMS_TABLE_COL_GROUP}
                            <TableHeader className='bg-white sticky top-0 z-10'>
                                <SortableTableHeader headers={TEAMS_TABLE_HEADERS} />
                            </TableHeader>
                        </Table>

                        <div className='h-[calc(100dvh-420px)] overflow-y-auto pl-2'>
                            <Table className='w-full table-fixed '>
                                {TEAMS_TABLE_COL_GROUP}
                                <TableBody className='w-full '>
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={5} className='h-[300px] text-center'>
                                                <Loader className='mx-auto animate-spin text-muted-foreground' />
                                            </TableCell>
                                        </TableRow>
                                    ) : teamMembers?.length > 0 ? (
                                        teamMembers?.map(team_member => {
                                            const { id, firstName, lastName, email, profilePicture, status, isOwner } =
                                                team_member || {};

                                            return (
                                                <TableRow
                                                    className='hover:bg-transparent cursor-default border-b'
                                                    key={id}
                                                >
                                                    <TableCell className='flex items-center gap-2 '>
                                                        <Avatar className='w-8 h-8'>
                                                            <AvatarImage src={profilePicture} alt={firstName} />
                                                            <AvatarFallback className='bg-gray-200 text-gray-500'>
                                                                {firstName?.charAt(0)}
                                                                {lastName?.charAt(0)}
                                                            </AvatarFallback>
                                                        </Avatar>

                                                        <span className='truncate max-w-xs text-gray900 text-sm font-medium capitalize'>
                                                            {firstName || '- '} {lastName || '-'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className='text-gray500 pl-3'>{email || '-'}</TableCell>

                                                    <TableCell className='pl-3'>
                                                        <TeamStatusBadge isOwner={isOwner} status={status} />
                                                    </TableCell>
                                                    <TableCell className='text-gray500'>
                                                        {globalOwner
                                                            ? renderTeamAction(isOwner, status, id, email)
                                                            : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow className='border-none'>
                                            <TableCell colSpan={5} className='h-[300px] text-center text-gray-400'>
                                                No team member found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    <TableRow className='hover:bg-transparent cursor-default'>
                                        <TableCell colSpan={5}>
                                            <div ref={ref} id='sentinel' className='py-6 text-center '>
                                                {isFetchingNextPage && 'Loading more...'}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </div>
            </div>
            <DeleteTeamConfirmation
                open={openConfirmDialog}
                onClose={setOpenConfirmDialog}
                onConfirm={handleConfirmDelete}
                isLoading={deleteApiLoading}
            />
        </>
    );
};

export { Teams };
